import type { ReactNode } from 'react';
import {
  Calendar,
  ChevronRight,
  Hash,
  Phone,
  Receipt,
  Search,
} from 'lucide-react';
import { Avatar } from '@/shared/ui/Avatar';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { formatIstDate, formatRupees } from '@/utils/format';
import { cn } from '@/shared/lib/utils';
import type {
  GlobalSearchAppointmentHit,
  GlobalSearchInvoiceHit,
  GlobalSearchPatientHit,
  GlobalSearchViewAllHit,
} from '../domain/global-search.types';
import {
  APPOINTMENT_STATUS_BADGE,
  appointmentStatusLabel,
  formatPhoneDisplay,
  INVOICE_STATUS_BADGE,
  invoiceStatusLabel,
} from './global-search-format';

interface PatientRowProps {
  hit: GlobalSearchPatientHit;
  index: number;
  active: boolean;
  onHighlight: (index: number) => void;
  onSelect: (href: string) => void;
}

export function GlobalSearchPatientRow({
  hit,
  index,
  active,
  onHighlight,
  onSelect,
}: PatientRowProps): JSX.Element {
  return (
    <Button
      type="button"
      variant="ghost"
      role="option"
      id={`global-search-hit-${index}`}
      aria-selected={active}
      className={cn(
        'global-search-option group h-auto w-full justify-start rounded-xl border-0 bg-transparent px-2.5 py-2.5 text-left hover:bg-transparent',
        active && 'global-search-option-active',
      )}
      onMouseEnter={() => onHighlight(index)}
      onClick={() => onSelect(hit.href)}
    >
      <Avatar name={hit.name} size="md" shape="rounded" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-semibold text-ink-900">{hit.name}</span>
          {hit.bloodGroup ? (
            <span className="flex-none rounded-md bg-mint-50 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-mint-700 ring-1 ring-mint-200/80">
              {hit.bloodGroup}
            </span>
          ) : null}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-500">
          {hit.patientDisplayId ? (
            <span className="inline-flex items-center gap-1 font-medium tabular-nums text-ink-600">
              <Hash className="size-3 text-ink-400" aria-hidden />
              {hit.patientDisplayId}
            </span>
          ) : null}
          {hit.phone ? (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <Phone className="size-3 text-ink-400" aria-hidden />
              {formatPhoneDisplay(hit.phone)}
            </span>
          ) : null}
        </div>
      </div>
      <ChevronRight
        className={cn(
          'size-4 flex-none text-ink-300 transition-all',
          active ? 'translate-x-0.5 text-brand-600' : 'opacity-0 group-hover:opacity-100',
        )}
        aria-hidden
      />
    </Button>
  );
}

interface AppointmentRowProps {
  hit: GlobalSearchAppointmentHit;
  index: number;
  active: boolean;
  onHighlight: (index: number) => void;
  onSelect: (href: string) => void;
}

export function GlobalSearchAppointmentRow({
  hit,
  index,
  active,
  onHighlight,
  onSelect,
}: AppointmentRowProps): JSX.Element {
  return (
    <Button
      type="button"
      variant="ghost"
      role="option"
      id={`global-search-hit-${index}`}
      aria-selected={active}
      className={cn(
        'global-search-option group h-auto w-full justify-start rounded-xl border-0 bg-transparent px-2.5 py-2.5 text-left hover:bg-transparent',
        active && 'global-search-option-active',
      )}
      onMouseEnter={() => onHighlight(index)}
      onClick={() => onSelect(hit.href)}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-mint-50 text-mint-700 ring-1 ring-mint-200/80">
        <Calendar className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[13px] font-semibold text-ink-900">{hit.patientName}</span>
          <Badge
            variant={APPOINTMENT_STATUS_BADGE[hit.status] ?? 'neutral'}
            className="rounded-md px-1.5 py-0 text-[10px]"
          >
            {appointmentStatusLabel(hit.status)}
          </Badge>
        </div>
        <div className="mt-1 truncate text-[11px] text-ink-500">
          {formatIstDate(hit.scheduledAt)}
          {hit.providerName ? ` · ${hit.providerName}` : ''}
        </div>
      </div>
      <ChevronRight
        className={cn(
          'size-4 flex-none text-ink-300 transition-all',
          active ? 'translate-x-0.5 text-brand-600' : 'opacity-0 group-hover:opacity-100',
        )}
        aria-hidden
      />
    </Button>
  );
}

interface InvoiceRowProps {
  hit: GlobalSearchInvoiceHit;
  index: number;
  active: boolean;
  onHighlight: (index: number) => void;
  onSelect: (href: string) => void;
}

export function GlobalSearchInvoiceRow({
  hit,
  index,
  active,
  onHighlight,
  onSelect,
}: InvoiceRowProps): JSX.Element {
  return (
    <Button
      type="button"
      variant="ghost"
      role="option"
      id={`global-search-hit-${index}`}
      aria-selected={active}
      className={cn(
        'global-search-option group h-auto w-full justify-start rounded-xl border-0 bg-transparent px-2.5 py-2.5 text-left hover:bg-transparent',
        active && 'global-search-option-active',
      )}
      onMouseEnter={() => onHighlight(index)}
      onClick={() => onSelect(hit.href)}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
        <Receipt className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[13px] font-semibold tabular-nums text-ink-900">
            {hit.displayNumber}
          </span>
          <Badge
            variant={INVOICE_STATUS_BADGE[hit.status] ?? 'neutral'}
            className="rounded-md px-1.5 py-0 text-[10px]"
          >
            {invoiceStatusLabel(hit.status)}
          </Badge>
        </div>
        <div className="mt-1 truncate text-[11px] text-ink-500">
          {[hit.patientName, hit.patientDisplayId].filter(Boolean).join(' · ') || 'Patient'}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span className="text-[13px] font-semibold tabular-nums text-ink-900">
          {formatRupees(hit.totalPaise)}
        </span>
        <ChevronRight
          className={cn(
            'size-3.5 text-ink-300 transition-all',
            active ? 'translate-x-0.5 text-brand-600' : 'opacity-0 group-hover:opacity-100',
          )}
          aria-hidden
        />
      </div>
    </Button>
  );
}

interface ViewAllRowProps {
  hit: GlobalSearchViewAllHit;
  index: number;
  active: boolean;
  onHighlight: (index: number) => void;
  onSelect: (href: string) => void;
}

export function GlobalSearchViewAllRow({
  hit,
  index,
  active,
  onHighlight,
  onSelect,
}: ViewAllRowProps): JSX.Element {
  return (
    <Button
      type="button"
      variant="ghost"
      role="option"
      id={`global-search-hit-${index}`}
      aria-selected={active}
      className={cn(
        'global-search-option group h-auto w-full justify-start gap-2 rounded-xl border-0 bg-transparent px-2.5 py-2.5 text-left text-[12px] font-medium text-brand-700 hover:bg-transparent',
        active && 'global-search-option-active',
      )}
      onMouseEnter={() => onHighlight(index)}
      onClick={() => onSelect(hit.href)}
    >
      <Search className="size-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{hit.label}</span>
      <ChevronRight className="size-3.5 shrink-0 text-brand-500" aria-hidden />
    </Button>
  );
}

interface SectionHeaderProps {
  icon: ReactNode;
  label: string;
  count: number;
  query: string;
}

export function GlobalSearchSectionHeader({
  icon,
  label,
  count,
  query,
}: SectionHeaderProps): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-surface-100 bg-surface-50/90 px-3.5 py-2">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-ink-600">
        <span className="text-ink-400">{icon}</span>
        {label}
        <span className="font-normal text-ink-400">
          {count} match{count === 1 ? '' : 'es'}
          {query ? <> for “{query}”</> : null}
        </span>
      </div>
    </div>
  );
}
